import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { feed, notifications as notifApi } from '../../lib/api';
import {
  Award,
  Bell,
  CheckCheck,
  Dumbbell,
  Heart,
  Link,
  Loader2,
  Lock,
  Megaphone,
  MessageSquare,
  Newspaper,
  RefreshCw,
  Rss,
  Send,
  ShieldAlert,
  Sparkles,
  Target,
  Trophy,
  Users,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

function urlB64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

async function registerPush(vapidPublicKey) {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return null;
  try {
    const reg = await navigator.serviceWorker.getRegistration('/sw.js');
    if (!reg) return null;
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return null;
    const existing = await reg.pushManager.getSubscription();
    if (existing) return existing;
    return await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlB64ToUint8Array(vapidPublicKey),
    });
  } catch (e) {
    console.warn('Push registration failed:', e);
    return null;
  }
}


const FEED_FILTERS = [
  { id: 'all', labelKey: 'feed.filters.all' },
  { id: 'announcement', labelKey: 'feed.filters.announcements' },
  { id: 'match', labelKey: 'feed.filters.matches' },
  { id: 'training', labelKey: 'feed.filters.training' },
  { id: 'achievement', labelKey: 'feed.filters.achievements' },
  { id: 'news', labelKey: 'feed.filters.news' },
];

const TYPE_META = {
  announcement: {
    labelKey: 'feed.types.announcement',
    icon: Megaphone,
    badge: 'bg-amber-50 text-amber-700 border-amber-200',
    accent: 'bg-amber-400',
  },
  news: {
    labelKey: 'feed.types.news',
    icon: Newspaper,
    badge: 'bg-blue-50 text-blue-700 border-blue-200',
    accent: 'bg-blue-400',
  },
  club: {
    labelKey: 'feed.types.club',
    icon: Users,
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    accent: 'bg-emerald-400',
  },
  tournament: {
    labelKey: 'feed.types.tournament',
    icon: Trophy,
    badge: 'bg-orange-50 text-orange-700 border-orange-200',
    accent: 'bg-orange-400',
  },
  match: {
    labelKey: 'feed.types.match',
    icon: Target,
    badge: 'bg-rose-50 text-rose-700 border-rose-200',
    accent: 'bg-rose-400',
  },
  training: {
    labelKey: 'feed.types.training',
    icon: Dumbbell,
    badge: 'bg-lime-50 text-lime-700 border-lime-200',
    accent: 'bg-lime-400',
  },
  achievement: {
    labelKey: 'feed.types.achievement',
    icon: Award,
    badge: 'bg-purple-50 text-purple-700 border-purple-200',
    accent: 'bg-purple-400',
  },
  system: {
    labelKey: 'feed.types.system',
    icon: Bell,
    badge: 'bg-zinc-100 text-zinc-700 border-zinc-200',
    accent: 'bg-zinc-500',
  },
  general: {
    labelKey: 'feed.types.general',
    icon: Rss,
    badge: 'bg-zinc-100 text-zinc-700 border-zinc-200',
    accent: 'bg-zinc-500',
  },
};

const getTypeMeta = (type) => TYPE_META[String(type || 'general').toLowerCase()] || TYPE_META.general;

const formatTypeLabel = (type, t) => t(getTypeMeta(type).labelKey);

const FeedBadge = ({ type, t }) => {
  const meta = getTypeMeta(type);
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${meta.badge}`}>
      <Icon size={13} />
      {t(meta.labelKey)}
    </span>
  );
};

const formatVisibility = (visibility, t) => {
  const key = String(visibility || '').toLowerCase();
  const known = {
    public: 'feed.visibility.public',
    private: 'feed.visibility.private',
    restricted: 'feed.visibility.restricted',
  };
  return known[key] ? t(known[key]) : visibility;
};

const FeedSkeleton = () => (
  <div className="space-y-4">
    {Array.from({ length: 3 }).map((_, index) => (
      <div key={index} className="animate-pulse rounded-3xl border border-zinc-200 bg-white p-5 shadow-sm">
        <div className="flex gap-3">
          <div className="h-12 w-12 rounded-2xl bg-zinc-100" />
          <div className="flex-1">
            <div className="h-4 w-44 rounded bg-zinc-100" />
            <div className="mt-3 h-3 w-28 rounded bg-zinc-100" />
          </div>
        </div>
        <div className="mt-5 h-5 w-3/4 rounded bg-zinc-100" />
        <div className="mt-3 h-3 w-full rounded bg-zinc-100" />
        <div className="mt-2 h-3 w-2/3 rounded bg-zinc-100" />
        <div className="mt-5 h-12 rounded-2xl bg-zinc-100" />
      </div>
    ))}
  </div>
);

const StatePanel = ({ icon: Icon, title, body, tone = 'zinc', action }) => {
  const toneClass = {
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    rose: 'bg-rose-50 border-rose-200 text-rose-800',
    zinc: 'bg-white border-zinc-200 text-zinc-700',
  }[tone];

  return (
    <div className={`rounded-3xl border p-6 text-center shadow-sm ${toneClass}`}>
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-zinc-950 shadow-sm">
        <Icon size={26} />
      </div>
      <h2 className="mt-5 text-xl font-semibold tracking-tight text-zinc-950">{title}</h2>
      <p className="mx-auto mt-2 max-w-xl text-sm leading-6">{body}</p>
      {action}
    </div>
  );
};

const MetricCard = ({ label, value, detail }) => (
  <div className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur">
    <p className="text-2xl font-semibold tracking-tight text-white">{value}</p>
    <p className="mt-1 text-xs font-semibold uppercase tracking-[0.16em] text-lime-200">{label}</p>
    {detail && <p className="mt-1 text-xs text-zinc-300">{detail}</p>}
  </div>
);

const CommentList = ({ comments, t }) => (
  <div className="space-y-3">
    {comments.map((comment) => (
      <div key={comment.id} className="flex gap-3 rounded-2xl bg-zinc-50 p-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-zinc-900 text-xs font-semibold text-lime-300">
          {comment.user?.name?.[0]?.toUpperCase() || 'U'}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-zinc-700">{comment.user?.name || t('feed.unknownUser')}</p>
          <p className="mt-0.5 text-sm leading-6 text-zinc-700">{comment.content}</p>
        </div>
      </div>
    ))}
    {comments.length === 0 && (
      <p className="rounded-2xl bg-zinc-50 px-4 py-3 text-sm text-zinc-500">
        {t('feed.noComments')}
      </p>
    )}
  </div>
);

const FeedPostCard = ({
  post,
  liked,
  expanded,
  copied,
  commentValue,
  comments,
  onLike,
  onToggleComments,
  onCopyLink,
  onCommentChange,
  onAddComment,
  formatDate,
  t,
}) => {
  const meta = getTypeMeta(post.content_type);
  const Icon = meta.icon;

  return (
    <article className={`overflow-hidden rounded-3xl border bg-white shadow-sm ${post.is_pinned ? 'border-amber-300 ring-1 ring-amber-200' : 'border-zinc-200'}`}>
      <div className={`h-1.5 ${meta.accent}`} />
      <div className="p-5 sm:p-6">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-zinc-950 text-lime-300">
            <Icon size={22} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <FeedBadge type={post.content_type} t={t} />
              {post.is_pinned && (
                <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">
                  {t('feed.pinned')}
                </span>
              )}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-zinc-500">
              <span className="font-semibold text-zinc-800">{post.user?.name || t('feed.unknownUser')}</span>
              <span>{formatDate(post.created_at)}</span>
              {post.visibility && <span>{formatVisibility(post.visibility, t)}</span>}
            </div>
          </div>
        </div>

        <div className="mt-5">
          <h3 className="text-xl font-semibold tracking-tight text-zinc-950">{post.title}</h3>
          {post.description && (
            <p className="mt-3 text-sm leading-7 text-zinc-600">{post.description}</p>
          )}
        </div>

        {post.image_urls?.length > 0 && (
          <div className="mt-5 overflow-hidden rounded-2xl border border-zinc-200 bg-zinc-100">
            <img src={post.image_urls[0]} alt={post.title || t('feed.postImageAlt')} className="h-72 w-full object-cover" />
          </div>
        )}

        <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-zinc-100 pt-4">
          <button
            type="button"
            onClick={onLike}
            className={`inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-semibold transition ${
              liked ? 'bg-rose-50 text-rose-600' : 'bg-zinc-100 text-zinc-600 hover:bg-rose-50 hover:text-rose-600'
            }`}
          >
            <Heart size={18} fill={liked ? 'currentColor' : 'none'} />
            {post.likes?.length || 0}
          </button>

          <button
            type="button"
            onClick={onToggleComments}
            className="inline-flex min-h-11 items-center gap-2 rounded-full bg-zinc-100 px-4 text-sm font-semibold text-zinc-600 transition hover:bg-lime-100 hover:text-lime-700"
          >
            <MessageSquare size={18} />
            {post.comments_count || 0}
          </button>

          <button
            type="button"
            onClick={onCopyLink}
            className="inline-flex min-h-11 items-center gap-2 rounded-full bg-zinc-100 px-4 text-sm font-semibold text-zinc-600 transition hover:bg-lime-100 hover:text-lime-700"
            title={t('feed.copyLink')}
          >
            {copied ? <CheckCheck size={18} className="text-emerald-600" /> : <Link size={18} />}
            {copied ? t('feed.copied') : t('feed.share')}
          </button>
        </div>

        {expanded && (
          <div className="mt-5 border-t border-zinc-100 pt-5">
            <CommentList comments={comments} t={t} />
            <div className="mt-4 flex gap-2">
              <input
                type="text"
                value={commentValue}
                onChange={(e) => onCommentChange(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onAddComment()}
                placeholder={t('feed.writeComment')}
                className="min-h-11 flex-1 rounded-2xl border border-zinc-200 bg-zinc-50 px-4 text-sm text-zinc-900 outline-none transition placeholder:text-zinc-400 focus:border-lime-400 focus:bg-white focus:ring-4 focus:ring-lime-100"
              />
              <button
                type="button"
                onClick={onAddComment}
                className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-2xl bg-zinc-950 text-lime-300 transition hover:bg-zinc-800"
              >
                <Send size={17} />
              </button>
            </div>
          </div>
        )}
      </div>
    </article>
  );
};

const FeedPage = () => {
  const { user } = useAuth();
  const { eventId } = useParams();
  const { t, i18n } = useTranslation();

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedPost, setExpandedPost] = useState(null);
  const [comments, setComments] = useState({});
  const [commentTextByPost, setCommentTextByPost] = useState({});
  const [copied, setCopied] = useState(null);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');

  const loadFeed = useCallback(async () => {
    if (!eventId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const res = await feed.getFeed(eventId);
      setPosts(Array.isArray(res.data) ? res.data : []);
      setForbidden(false);
    } catch (err) {
      if (err?.response?.status === 403) {
        setForbidden(true);
        setPosts([]);
      } else {
        setError(err);
      }
      console.error('Failed to load feed:', err);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { loadFeed(); }, [loadFeed]);

  useEffect(() => {
    if (!eventId || typeof window === 'undefined') return;
    window.localStorage.setItem('td:last-feed-event-id', eventId);
  }, [eventId]);

  useEffect(() => {
    notifApi.getSettings().then((res) => {
      setPushEnabled(!!res.data?.push_enabled);
    }).catch(() => {});
  }, []);

  const filteredPosts = useMemo(() => {
    if (activeFilter === 'all') return posts;
    return posts.filter((post) => String(post.content_type || '').toLowerCase() === activeFilter);
  }, [posts, activeFilter]);

  const postTypes = useMemo(() => new Set(posts.map((post) => String(post.content_type || '').toLowerCase())), [posts]);

  const stats = useMemo(() => {
    const announcements = posts.filter((post) => post.content_type === 'announcement').length;
    const interactions = posts.reduce(
      (sum, post) => sum + (post.likes?.length || 0) + (post.comments_count || 0),
      0
    );
    return { total: posts.length, announcements, interactions };
  }, [posts]);

  const handleLike = async (postId, isLiked) => {
    if (isLiked) return;
    try {
      await feed.like(eventId, postId);
      loadFeed();
    } catch (err) {
      console.error('Like failed:', err);
    }
  };

  const handleToggleComments = async (postId) => {
    if (expandedPost === postId) {
      setExpandedPost(null);
      return;
    }
    setExpandedPost(postId);
    if (!comments[postId]) {
      try {
        const res = await feed.getPost(eventId, postId);
        setComments((prev) => ({ ...prev, [postId]: res.data?.comments || [] }));
      } catch (err) {
        console.error('Failed to load comments:', err);
      }
    }
  };

  const handleAddComment = async (postId) => {
    const content = (commentTextByPost[postId] || '').trim();
    if (!content) return;
    try {
      await feed.addComment(eventId, postId, { post_id: postId, content });
      setCommentTextByPost((prev) => ({ ...prev, [postId]: '' }));
      const res = await feed.getPost(eventId, postId);
      setComments((prev) => ({ ...prev, [postId]: res.data?.comments || [] }));
      loadFeed();
    } catch (err) {
      console.error('Comment failed:', err);
    }
  };

  const handleCopyLink = (postId) => {
    const url = `${window.location.origin}/events/${eventId}/feed/${postId}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(postId);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const handleEnablePush = async () => {
    try {
      const keyRes = await notifApi.push.getVapidKey();
      const vapidKey = keyRes.data?.publicKey;
      if (!vapidKey) return;
      const sub = await registerPush(vapidKey);
      if (!sub) return;
      await notifApi.push.subscribe(sub.toJSON());
      setPushEnabled(true);
    } catch (err) {
      console.error('Push enable failed:', err);
    }
  };

  const handleDisablePush = async () => {
    try {
      if ('serviceWorker' in navigator) {
        const reg = await navigator.serviceWorker.getRegistration('/sw.js');
        if (reg) {
          const sub = await reg.pushManager.getSubscription();
          if (sub) {
            await notifApi.push.unsubscribe(sub.endpoint);
            await sub.unsubscribe();
          }
        }
      }
      setPushEnabled(false);
    } catch (err) {
      console.error('Push disable failed:', err);
    }
  };

  const localeDateString = (dateStr) => {
    const locale = i18n.language === 'ru' ? 'ru-RU' : i18n.language === 'fr' ? 'fr-FR' : 'en-GB';
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const isLiked = (post) => post.likes?.includes(user?.id);

  if (!eventId) {
    return (
      <div className="min-h-screen bg-[#f7f8f4] px-4 py-6 pb-24 lg:px-8 lg:py-8 lg:pb-8">
        <div className="mx-auto max-w-4xl">
          <StatePanel
            icon={ShieldAlert}
            title={t('feed.eventRequired')}
            body={t('feed.entryResolverHint')}
            tone="amber"
          />
        </div>
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className="min-h-screen bg-[#f7f8f4] px-4 py-6 pb-24 lg:px-8 lg:py-8 lg:pb-8">
        <div className="mx-auto max-w-4xl">
          <StatePanel
            icon={Lock}
            title={t('feed.rosterRestricted')}
            body={t('feed.rosterRestrictedBody')}
            tone="zinc"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f8f4] px-4 py-5 pb-24 lg:px-8 lg:py-8 lg:pb-8">
      <div className="mx-auto max-w-5xl space-y-6">
        <section className="overflow-hidden rounded-[2rem] bg-zinc-950 text-white shadow-xl">
          <div className="relative p-6 sm:p-8">
            <div className="pointer-events-none absolute inset-0 opacity-25">
              <div className="absolute -right-16 top-0 h-64 w-64 rounded-full border border-lime-300/40" />
              <div className="absolute bottom-5 left-12 h-px w-64 bg-lime-300/40" />
              <div className="absolute bottom-8 right-20 h-28 w-64 rounded-[50%] border border-lime-300/20" />
            </div>

            <div className="relative grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-end">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-lime-200">
                  <Rss size={14} />
                  {t('feed.eventFeed')}
                </div>
                <h1 className="mt-5 text-4xl font-semibold tracking-tight sm:text-5xl">{t('feed.title')}</h1>
                <p className="mt-4 max-w-2xl text-sm leading-7 text-zinc-300">
                  {t('feed.subtitle')}
                </p>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <MetricCard label={t('feed.posts')} value={stats.total} />
                <MetricCard label={t('feed.pinned')} value={posts.filter((post) => post.is_pinned).length} />
                <MetricCard label={t('feed.replies')} value={stats.interactions} />
              </div>
            </div>
          </div>
        </section>

        <section className="flex flex-col gap-3 rounded-3xl border border-zinc-200 bg-white p-3 shadow-sm lg:flex-row lg:items-center lg:justify-between">
          <div className="flex gap-2 overflow-x-auto p-1">
            {FEED_FILTERS.map((filter) => {
              const disabled = filter.id !== 'all' && !postTypes.has(filter.id);
              return (
                <button
                  type="button"
                  key={filter.id}
                  onClick={() => setActiveFilter(filter.id)}
                  disabled={disabled}
                  className={`min-h-11 whitespace-nowrap rounded-2xl px-4 text-sm font-semibold transition ${
                    activeFilter === filter.id
                      ? 'bg-zinc-950 text-lime-300'
                      : disabled
                        ? 'text-zinc-300'
                        : 'text-zinc-600 hover:bg-zinc-100 hover:text-zinc-950'
                  }`}
                >
                  {t(filter.labelKey)}
                </button>
              );
            })}
          </div>

          <button
            type="button"
            onClick={pushEnabled ? handleDisablePush : handleEnablePush}
            className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl px-4 text-sm font-semibold transition ${
              pushEnabled
                ? 'bg-lime-100 text-lime-700'
                : 'bg-zinc-100 text-zinc-700 hover:bg-lime-100 hover:text-lime-700'
            }`}
          >
            <Bell size={17} />
            {pushEnabled ? t('feed.pushOn') : t('feed.pushOff')}
          </button>
        </section>

        <section className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_280px]">
          <div>
            {loading ? (
              <FeedSkeleton />
            ) : error ? (
              <StatePanel
                icon={RefreshCw}
                title={t('feed.loadErrorTitle')}
                body={t('feed.loadErrorBody')}
                tone="rose"
                action={(
                  <button
                    type="button"
                    onClick={loadFeed}
                    className="mt-5 inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-zinc-950 px-5 text-sm font-semibold text-white transition hover:bg-zinc-800"
                  >
                    <RefreshCw size={16} />
                    {t('feed.retry')}
                  </button>
                )}
              />
            ) : filteredPosts.length === 0 ? (
              <StatePanel
                icon={MessageSquare}
                title={posts.length === 0 ? t('feed.noPostsYet') : t('feed.noFilteredPosts', { type: formatTypeLabel(activeFilter, t) })}
                body={posts.length === 0 ? t('feed.eventEmptyBody') : t('feed.tryAllFilter')}
                tone="zinc"
              />
            ) : (
              <div className="space-y-4">
                {filteredPosts.map((post) => (
                  <FeedPostCard
                    key={post.id}
                    post={post}
                    liked={isLiked(post)}
                    expanded={expandedPost === post.id}
                    copied={copied === post.id}
                    commentValue={commentTextByPost[post.id] || ''}
                    comments={comments[post.id] || []}
                    onLike={() => handleLike(post.id, isLiked(post))}
                    onToggleComments={() => handleToggleComments(post.id)}
                    onCopyLink={() => handleCopyLink(post.id)}
                    onCommentChange={(value) => setCommentTextByPost((prev) => ({ ...prev, [post.id]: value }))}
                    onAddComment={() => handleAddComment(post.id)}
                    formatDate={localeDateString}
                    t={t}
                  />
                ))}
              </div>
            )}
          </div>

          <aside className="space-y-4">
            <div className="rounded-3xl border border-zinc-200 bg-white p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-lime-100 text-lime-700">
                  <Sparkles size={21} />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-zinc-950">{t('feed.streamFocus')}</h2>
                  <p className="text-sm text-zinc-500">{t('feed.eventScopedUpdates')}</p>
                </div>
              </div>
              <div className="mt-5 space-y-3">
                {Object.entries(TYPE_META).slice(0, 7).map(([type]) => (
                  <div key={type} className="flex items-center justify-between gap-3 rounded-2xl bg-zinc-50 px-3 py-2">
                    <FeedBadge type={type} t={t} />
                    <span className="text-sm font-semibold text-zinc-600">
                      {posts.filter((post) => String(post.content_type || '').toLowerCase() === type).length}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-3xl border border-zinc-200 bg-white p-5 shadow-sm">
              <h2 className="text-base font-semibold text-zinc-950">{t('feed.supportedActions')}</h2>
              <p className="mt-2 text-sm leading-6 text-zinc-600">
                {t('feed.supportedActionsBody')}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {['like', 'comment', 'share', 'push'].map((action) => (
                  <span key={action} className="rounded-full bg-zinc-100 px-3 py-1.5 text-xs font-semibold text-zinc-700">
                    {t(`feed.actions.${action}`)}
                  </span>
                ))}
              </div>
            </div>
          </aside>
        </section>
      </div>
    </div>
  );
};

export default FeedPage;
